import { useState } from 'react';
import { LuFileText } from 'react-icons/lu';
import Drawer from '../Drawer/Drawer';
import StatusBadge from '../StatusBadge/StatusBadge';
import CustomSelect from '../CustomSelect/CustomSelect';
import { getAssetUrl } from '../../lib/directus';
import styles from './BatchDrawer.module.css';

const REJECTION_REASONS = ['Grade mismatch', 'Quantity mismatch', 'Invalid certificate', 'Incomplete documentation', 'Other'];

function Field({ label, value }) {
  return (
    <div>
      <p className={styles.fieldLabel}>{label}</p>
      <p className={styles.fieldValue}>{value}</p>
    </div>
  );
}

export default function BatchDrawer({ batch, onClose, onApprove, onReject }) {
  const [rejectionReason, setRejectionReason] = useState('Select type');

  return (
    <Drawer
      isOpen={!!batch}
      title="Batch details"
      onClose={onClose}
      footer={
        <>
          <button className={styles.btnReject} onClick={() => onReject && onReject(batch, rejectionReason)}>
            Reject batch
          </button>
          <button className={styles.btnApprove} onClick={() => onApprove && onApprove(batch)}>
            Approve & list
          </button>
        </>
      }
    >
      {batch && (
        <>
          {/* Batch details card */}
          <div className={styles.card}>
            <div className={styles.grid}>
              <Field label="Commodity" value={batch.commodity || '-'} />
              <Field label="Grade"     value={batch.grade || '-'} />
              <Field label="Quantity"  value={batch.qty || '-'} />
              <div>
                <p className={styles.fieldLabel}>Warehouse</p>
                <p className={styles.fieldValue}>{batch.warehouse || '-'}</p>
              </div>
              <div>
                <p className={styles.fieldLabel}>Status</p>
                <StatusBadge status={batch.status} />
              </div>
            </div>
          </div>

          {/* Quality specifications */}
          {(batch.moistureContent || batch.foreignMatter || batch.brokenDamaged) && (
            <div className={styles.card}>
              <p className={styles.cardTitle}>Quality specifications</p>
              <div className={styles.grid}>
                <Field label="Moisture Content" value={batch.moistureContent || '-'} />
                <Field label="Foreign Matter"   value={batch.foreignMatter   || '-'} />
                <Field label="Broken/Damaged"   value={batch.brokenDamaged   || '-'} />
              </div>
            </div>
          )}

          {/* Seller information */}
          <div className={styles.card}>
            <p className={styles.cardTitle}>Seller information</p>
            <div className={styles.grid}>
              <Field label="Company"      value={batch.seller || '-'} />
              <Field label="Submitted at" value={batch.submittedAt || '-'} />
            </div>
          </div>

          {/* Certificates */}
          {batch.certificates?.length > 0 && (
            <div className={styles.card}>
              <p className={styles.cardTitle}>Certificates</p>
              {batch.certificates.map((cert, i) => {
                const fileId = cert?.directus_files_id?.id || cert?.directus_files_id || cert?.id;
                const fileName = cert?.directus_files_id?.filename_download || `Certificate ${i + 1}`;
                const fileSize = cert?.directus_files_id?.filesize
                  ? `${(cert.directus_files_id.filesize / (1024 * 1024)).toFixed(1)} MB`
                  : null;
                return (
                  <div key={cert.id || i} className={styles.docRow}>
                    <div className={styles.docLeft}>
                      <LuFileText size={18} className={styles.docIcon} />
                      <div>
                        <p className={styles.docName}>{fileName}</p>
                        {fileSize && <p className={styles.docMeta}>{fileSize} • Uploaded</p>}
                      </div>
                    </div>
                    {fileId && (
                      <a
                        href={getAssetUrl(fileId)}
                        target="_blank"
                        rel="noreferrer"
                        className={styles.viewLink}
                      >
                        View
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Rejection reason */}
          <div className={styles.card}>
            <div className={styles.rejectionSection}>
              <p className={styles.rejectionLabel}>Rejection Reason (if applicable)</p>
              <CustomSelect
                value={rejectionReason}
                onChange={setRejectionReason}
                options={['Select type', ...REJECTION_REASONS]}
              />
            </div>
          </div>
        </>
      )}
    </Drawer>
  );
}
